import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getInvoices,
  createInvoiceFromQuote,
  createInvoiceManual,
  updateInvoice,
  deleteInvoice,
  getQuotes,
  getProjects,
  getClients,
  getInvoicePdfUrl,
  viewInvoicePdf,
  downloadInvoicePdf,
  extractErrorMessage,
} from "../lib/api";
import {
  PageHeader,
  DataTable,
  StatusBadge,
  LoadingSpinner,
  EmptyState,
  LockedFieldNotice,
} from "../components/shared/DataComponents";
import { useFormatters } from "../components/shared/useFormatters";
import { DestructiveConfirmDialog } from "../components/shared/DestructiveConfirmDialog";
import { ClientSelect } from "../components/shared/ClientSelect";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Plus, Receipt, Pencil, Trash2, X, Lock, Loader2, FileText, Download, Eye } from "lucide-react";

export default function InvoicesPage() {
  const navigate = useNavigate();
  const { formatDate, formatCurrency, currencySymbol } = useFormatters();
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [createMode, setCreateMode] = useState("quote"); // "quote" or "manual"
  const [formData, setFormData] = useState({
    quote_id: "",
    project_id: "",
    client_id: "",
    issue_date: new Date().toISOString().split("T")[0],
    due_date: "",
    tax_rate: "20",
    notes: "",
    status: "draft",
    amount_paid: "",
    lines: [{ description: "", quantity: "1", unit_price: "", line_total: 0 }],
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [invRes, quoteRes, projRes, clientRes] = await Promise.all([
        getInvoices(),
        getQuotes(),
        getProjects(),
        getClients(),
      ]);
      setInvoices(invRes.data);
      setQuotes(quoteRes.data.filter((q) => q.status === "approved"));
      setProjects(projRes.data);
      setClients(clientRes.data);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    return client?.name || "—";
  };

  const getProjectName = (projectId) => {
    if (!projectId) return "—";
    const project = projects.find((p) => p.id === projectId);
    return project?.name || "—";
  };

  const handleOpenDialog = (invoice = null) => {
    if (invoice) {
      setSelectedInvoice(invoice);
      setFormData({
        ...formData,
        status: invoice.status,
        due_date: invoice.due_date,
        amount_paid: invoice.amount_paid?.toString() || "0",
        notes: invoice.notes || "",
      });
    } else {
      setSelectedInvoice(null);
      setFormData({
        quote_id: "",
        project_id: "",
        client_id: "",
        issue_date: new Date().toISOString().split("T")[0],
        due_date: "",
        tax_rate: "20",
        notes: "",
        status: "draft",
        amount_paid: "",
        lines: [{ description: "", quantity: "1", unit_price: "", line_total: 0 }],
      });
    }
    setDialogOpen(true);
  };

  const updateLine = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index][field] = value;
    
    const qty = parseFloat(newLines[index].quantity) || 0;
    const price = parseFloat(newLines[index].unit_price) || 0;
    newLines[index].line_total = qty * price;
    
    setFormData({ ...formData, lines: newLines });
  };

  const addLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { description: "", quantity: "1", unit_price: "", line_total: 0 }],
    });
  };

  const removeLine = (index) => {
    if (formData.lines.length > 1) {
      const newLines = formData.lines.filter((_, i) => i !== index);
      setFormData({ ...formData, lines: newLines });
    }
  };

  const calculateTotals = () => {
    const subtotal = formData.lines.reduce((sum, line) => sum + (line.line_total || 0), 0);
    const taxRate = parseFloat(formData.tax_rate) || 0;
    const taxAmount = subtotal * (taxRate / 100);
    const total = subtotal + taxAmount;
    return { subtotal, taxAmount, total };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (selectedInvoice) {
        await updateInvoice(selectedInvoice.id, {
          status: formData.status,
          due_date: formData.due_date,
          amount_paid: parseFloat(formData.amount_paid) || 0,
          notes: formData.notes,
        });
        toast.success("Invoice updated successfully");
      } else if (createMode === "quote") {
        await createInvoiceFromQuote({
          quote_id: formData.quote_id,
          issue_date: formData.issue_date,
          due_date: formData.due_date,
          notes: formData.notes,
        });
        toast.success("Invoice created from quote");
      } else {
        const lines = formData.lines.map((line) => ({
          description: line.description,
          quantity: parseFloat(line.quantity),
          unit_price: parseFloat(line.unit_price),
          line_total: line.line_total,
        }));
        
        await createInvoiceManual({
          project_id: formData.project_id,
          client_id: formData.client_id,
          issue_date: formData.issue_date,
          due_date: formData.due_date,
          tax_rate: parseFloat(formData.tax_rate),
          notes: formData.notes,
          lines,
        });
        toast.success("Invoice created successfully");
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusUpdate = async (invoice, newStatus) => {
    try {
      await updateInvoice(invoice.id, { status: newStatus });
      toast.success(`Invoice marked as ${newStatus}`);
      loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteInvoice(selectedInvoice.id);
      toast.success("Invoice deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedInvoice(null);
      loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  // Check if invoice is locked (sent/paid/overdue)
  const isInvoiceLocked = (invoice) => {
    return invoice && ["sent", "paid", "overdue"].includes(invoice.status);
  };

  const columns = [
    { 
      key: "invoice_number", 
      label: "Invoice #",
      render: (value, row) => (
        <button
          onClick={() => navigate(`/invoices/${row.id}`)}
          className="text-primary hover:underline font-medium"
          data-testid={`invoice-link-${row.id}`}
        >
          {value}
        </button>
      ),
    },
    {
      key: "client_id",
      label: "Client",
      render: (value) => getClientName(value),
    },
    {
      key: "project_id",
      label: "Project",
      render: (value) => getProjectName(value),
    },
    {
      key: "issue_date",
      label: "Issued",
      render: (value) => formatDate(value),
    },
    {
      key: "due_date",
      label: "Due",
      render: (value) => formatDate(value),
    },
    {
      key: "total",
      label: "Total",
      align: "right",
      render: (value) => <span className="font-mono">{formatCurrency(value)}</span>,
    },
    {
      key: "amount_paid",
      label: "Paid",
      align: "right",
      render: (value) => <span className="font-mono">{formatCurrency(value)}</span>,
    },
    {
      key: "status",
      label: "Status",
      render: (value) => <StatusBadge status={value} />,
    },
    {
      key: "actions",
      label: "",
      align: "right",
      render: (_, row) => {
        const locked = isInvoiceLocked(row);
        const canDelete = row.status === "draft";
        return (
          <div className="flex items-center justify-end gap-2">
            {row.status === "draft" && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleStatusUpdate(row, "sent");
                }}
                className="text-xs"
              >
                Send
              </Button>
            )}
            {row.status === "sent" && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleStatusUpdate(row, "paid");
                }}
                className="text-xs text-emerald-500"
              >
                Mark Paid
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/invoices/${row.id}`);
              }}
              data-testid={`view-invoice-${row.id}`}
              title="View invoice"
            >
              <Eye className="h-4 w-4 text-primary" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                viewInvoicePdf(row.id, row.invoice_number).catch(() => toast.error('Failed to open PDF'));
              }}
              data-testid={`view-invoice-pdf-${row.id}`}
              title="View PDF"
            >
              <FileText className="h-4 w-4 text-teal-500" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                downloadInvoicePdf(row.id, row.invoice_number).catch(() => toast.error('Failed to download PDF'));
              }}
              data-testid={`download-invoice-pdf-${row.id}`}
              title="Download PDF"
            >
              <Download className="h-4 w-4 text-teal-500" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDialog(row);
              }}
              data-testid={`edit-invoice-${row.id}`}
              title={locked ? "View invoice details" : "Edit invoice"}
            >
              {locked ? <Lock className="h-4 w-4 text-muted-foreground" /> : <Pencil className="h-4 w-4" />}
            </Button>
            {canDelete ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedInvoice(row);
                  setDeleteDialogOpen(true);
                }}
                data-testid={`delete-invoice-${row.id}`}
              >
                <Trash2 className="h-4 w-4 text-red-400" />
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                disabled
                title="Only draft invoices can be deleted"
              >
                <Trash2 className="h-4 w-4 text-muted-foreground/30" />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const canCreate = (quotes.length > 0) || (projects.length > 0 && clients.length > 0);
  const { subtotal, taxAmount, total } = calculateTotals();

  if (loading) {
    return <LoadingSpinner size="lg" />;
  }

  return (
    <div data-testid="invoices-page">
      <PageHeader
        title="Invoices"
        subtitle="Invoice management with snapshotted values"
        actions={
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-medium tracking-wide uppercase text-xs px-6"
            data-testid="add-invoice-btn"
            disabled={!canCreate}
          >
            <Plus className="h-4 w-4 mr-2" />
            New Invoice
          </Button>
        }
      />

      <div className="bg-card border border-white/5 rounded-sm">
        {invoices.length === 0 ? (
          <EmptyState
            icon={Receipt}
            title="No invoices yet"
            description="Create invoices from approved quotes or manually."
            action={
              canCreate && (
                <Button
                  onClick={() => handleOpenDialog()}
                  className="bg-primary text-primary-foreground"
                  data-testid="empty-add-invoice-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Invoice
                </Button>
              )
            }
          />
        ) : (
          <DataTable columns={columns} data={invoices} />
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-secondary text-xl uppercase tracking-wide">
              {selectedInvoice ? `Invoice ${selectedInvoice.invoice_number}` : "New Invoice"}
            </DialogTitle>
          </DialogHeader>
          
          {!selectedInvoice ? (
            <Tabs value={createMode} onValueChange={setCreateMode}>
              <TabsList className="w-full">
                <TabsTrigger value="quote" className="flex-1">From Quote</TabsTrigger>
                <TabsTrigger value="manual" className="flex-1">Manual</TabsTrigger>
              </TabsList>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <TabsContent value="quote" className="space-y-4">
                  <div>
                    <Label htmlFor="quote_id">Approved Quote *</Label>
                    <Select
                      value={formData.quote_id}
                      onValueChange={(value) => setFormData({ ...formData, quote_id: value })}
                    >
                      <SelectTrigger data-testid="invoice-quote-select">
                        <SelectValue placeholder="Select approved quote" />
                      </SelectTrigger>
                      <SelectContent>
                        {quotes.map((quote) => (
                          <SelectItem key={quote.id} value={quote.id}>
                            {quote.quote_number} - {formatCurrency(quote.total)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {quotes.length === 0 && (
                      <p className="text-xs text-amber-500 mt-1">No approved quotes available</p>
                    )}
                  </div>
                </TabsContent>
                
                <TabsContent value="manual" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="client_id">Client *</Label>
                      <ClientSelect
                        clients={clients}
                        value={formData.client_id}
                        onChange={(value) => setFormData({ ...formData, client_id: value || '' })}
                        placeholder="Search or select client..."
                        testId="invoice-client-select"
                      />
                    </div>
                    <div>
                      <Label htmlFor="project_id">Project *</Label>
                      <Select
                        value={formData.project_id}
                        onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                      >
                        <SelectTrigger data-testid="invoice-project-select">
                          <SelectValue placeholder="Select project" />
                        </SelectTrigger>
                        <SelectContent>
                          {projects.map((project) => (
                            <SelectItem key={project.id} value={project.id}>
                              {project.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="tax_rate">Tax Rate (%)</Label>
                    <Input
                      id="tax_rate"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.tax_rate}
                      onChange={(e) => setFormData({ ...formData, tax_rate: e.target.value })}
                      data-testid="invoice-tax-rate-input"
                    />
                  </div>

                  {/* Line Items */}
                  <div>
                    <Label>Line Items</Label>
                    <div className="space-y-2 mt-2">
                      {formData.lines.map((line, idx) => (
                        <div key={idx} className="flex gap-2 items-start">
                          <Input
                            value={line.description}
                            onChange={(e) => updateLine(idx, "description", e.target.value)}
                            placeholder="Description"
                            className="flex-1"
                            required
                            data-testid={`invoice-line-desc-${idx}`}
                          />
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={line.quantity}
                            onChange={(e) => updateLine(idx, "quantity", e.target.value)}
                            placeholder="Qty"
                            className="w-20"
                            required
                          />
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={line.unit_price}
                            onChange={(e) => updateLine(idx, "unit_price", e.target.value)}
                            placeholder="Price"
                            className="w-24"
                            required
                          />
                          <span className="font-mono text-sm w-24 text-right pt-2">
                            {formatCurrency(line.line_total)}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeLine(idx)}
                            disabled={formData.lines.length === 1}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addLine}
                      className="mt-2"
                      data-testid="invoice-add-line-btn"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add Line
                    </Button>
                  </div>

                  {/* Totals */}
                  <div className="border-t border-white/10 pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="font-mono">{formatCurrency(subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Tax ({formData.tax_rate}%)</span>
                      <span className="font-mono">{formatCurrency(taxAmount)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-semibold">
                      <span>Total</span>
                      <span className="font-mono">{formatCurrency(total)}</span>
                    </div>
                  </div>
                </TabsContent>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="issue_date">Issue Date *</Label>
                    <Input
                      id="issue_date"
                      type="date"
                      value={formData.issue_date}
                      onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                      required
                      data-testid="invoice-issue-date-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="due_date">Due Date *</Label>
                    <Input
                      id="due_date"
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      required
                      data-testid="invoice-due-date-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes"
                    rows={2}
                    data-testid="invoice-notes-input"
                  />
                </div>
                
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-primary text-primary-foreground" data-testid="invoice-submit-btn" disabled={submitting}>
                    {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Create Invoice
                  </Button>
                </DialogFooter>
              </form>
            </Tabs>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="bg-surface-elevated/30 p-4 rounded-sm">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Invoice Details (Locked)</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Client:</span>{" "}
                    {getClientName(selectedInvoice.client_id)}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Project:</span>{" "}
                    {getProjectName(selectedInvoice.project_id)}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Total:</span>{" "}
                    <span className="font-mono">{formatCurrency(selectedInvoice.total)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Outstanding:</span>{" "}
                    <span className="font-mono">
                      {formatCurrency(selectedInvoice.total - selectedInvoice.amount_paid)}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="overdue">Overdue</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="amount_paid">Amount Paid ({currencySymbol})</Label>
                  <Input
                    id="amount_paid"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount_paid}
                    onChange={(e) => setFormData({ ...formData, amount_paid: e.target.value })}
                    data-testid="invoice-amount-paid-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="due_date">Due Date</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                />
              </div>
              
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes"
                  rows={2}
                />
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary text-primary-foreground" disabled={submitting}>
                  {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Update Invoice
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <DestructiveConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Invoice"
        itemName={selectedInvoice?.invoice_number}
        itemType="invoice"
        description={`Are you sure you want to delete invoice "${selectedInvoice?.invoice_number}"? Only draft invoices can be deleted. This action is permanent and cannot be undone.`}
        onConfirm={handleDelete}
        loading={deleting}
      />
    </div>
  );
}
